import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { api } from "@/lib/api";

import Login from "@/pages/Login";
import { Privacidade, Termos } from "@/pages/Legal";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import DocumentReader from "@/pages/DocumentReader";
import OCRReview from "@/pages/OCRReview";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import DocumentChoice from "@/pages/DocumentChoice";
import ContractForm from "@/pages/ContractForm";
import ProcuracaoForm from "@/pages/ProcuracaoForm";
import DocumentEditor from "@/pages/DocumentEditor";
import Sidebar from "@/components/Sidebar";
import Templates from "@/pages/Templates";
import SignPublic from "@/pages/SignPublic";
import Kanban from "@/pages/Kanban";
import DocTemplates from "@/pages/DocTemplates";
import VerifyPublic from "@/pages/VerifyPublic";
import VerifyCalculation from "@/pages/VerifyCalculation";
import KanbanPrazos from "@/pages/KanbanPrazos";
import ProcessoDetail from "@/pages/ProcessoDetail";
import PreparadoHermes from "@/pages/PreparadoHermes";
import ValePena from "@/pages/ValePena";
import { MineralLayout, MineralHome, MineralPlaceholder } from "@/pages/mineral/MineralLayout";
import MineralAdminCommerce from "@/pages/mineral/MineralAdminCommerce";
import MineralMap from "@/pages/mineral/MineralMap";
import ConsultaMineralPublica from "@/pages/mineral/ConsultaMineralPublica";
import ConsultaMineralAguardando from "@/pages/mineral/ConsultaMineralAguardando";
import RelatorioMineralPublico from "@/pages/mineral/RelatorioMineralPublico";
import MeusPedidosGuest from "@/pages/mineral/MeusPedidosGuest";
import ConsultaLoteMineral from "@/pages/mineral/ConsultaLoteMineral";
import ExtratoCreditosMineral from "@/pages/mineral/ExtratoCreditosMineral";
import DashboardRadarMineral from "@/pages/mineral/DashboardRadarMineral";
import Inteligencia360 from "@/pages/Inteligencia360";
import IntelligenceOrders from "@/pages/IntelligenceOrders";
import IntelligenceWatches from "@/pages/IntelligenceWatches";
import Fase6Legislativo from "@/pages/Fase6Legislativo";
import Fase6Contratacoes from "@/pages/Fase6Contratacoes";
import RadarRisco from "@/pages/RadarRisco";
import AuthEmailForm from "@/pages/AuthEmailForm";
import { ForgotPassword, ResetPassword } from "@/pages/AuthPasswordReset";
import Settings from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import Jurisprudencia from "@/pages/Jurisprudencia";
import { PlansPage, PaymentSuccess, PaymentCancel } from "@/pages/Plans";
import MyPlan from "@/pages/MyPlan";
import Finance from "@/pages/Finance";
import Cobrancas from "@/pages/Cobrancas";
import Referrals from "@/pages/Referrals";
import ReferralCapture from "@/pages/ReferralCapture";
import SentenceUpdates from "@/pages/SentenceUpdates";
import CalculosHome from "@/pages/calculos/CalculosHome";
import LiquidacaoList from "@/pages/calculos/LiquidacaoList";
import LiquidacaoNova from "@/pages/calculos/LiquidacaoNova";
import LiquidacaoDetail from "@/pages/calculos/LiquidacaoDetail";
import LiquidacaoParametros from "@/pages/calculos/LiquidacaoParametros";
import LiquidacaoTitulo from "@/pages/calculos/LiquidacaoTitulo";
import LiquidacaoCalculo from "@/pages/calculos/LiquidacaoCalculo";
import Djen from "@/pages/Djen";
import Prazos from "@/pages/Prazos";
import DeadlineAlert from "@/components/DeadlineAlert";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminGeo from "@/pages/admin/AdminGeo";
import AdminBirthdays from "@/pages/admin/AdminBirthdays";
import AdminFinancial from "@/pages/admin/AdminFinancial";
import AdminCommunications from "@/pages/admin/AdminCommunications";
import AdminTemplates from "@/pages/admin/AdminTemplates";
import AdminIntelligence from "@/pages/admin/AdminIntelligence";
import AdminCreditosMineral from "@/pages/admin/AdminCreditosMineral";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminDjen from "@/pages/admin/AdminDjen";
import AdminImages from "@/pages/admin/AdminImages";
import AdminComercial from "@/pages/admin/AdminComercial";
import AdminApisHealth from "@/pages/admin/AdminApisHealth";
import WalletTopupSuccess from "@/pages/WalletTopupSuccess";
import PlanSuccess from "@/pages/PlanSuccess";
import PreparadoDetail from "@/pages/PreparadoDetail";
import PreparadosList from "@/pages/PreparadosList";
import PreparadosCompare from "@/pages/PreparadosCompare";
import ModelosConverge from "@/pages/ModelosConverge";
import Noticias from "@/pages/Noticias";
import Jogos from "@/pages/Jogos";
import FinanceHub from "@/pages/FinanceHub";
import ModelosHub from "@/pages/ModelosHub";
import AdminStub from "@/pages/admin/AdminStub";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const hash = location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) { navigate("/"); return; }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    api.post("/auth/session", { session_id: sid })
      .then(async () => {
        // Attach any pending referral code captured by /r/:code
        try {
          const ref = localStorage.getItem("hermes_ref");
          if (ref) {
            await api.post("/referrals/attach", { code: ref }).catch(() => null);
            localStorage.removeItem("hermes_ref");
          }
        } catch {}
        window.history.replaceState({}, "", "/dashboard");
        window.location.replace("/dashboard");
      })
      .catch(() => { navigate("/"); });
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-600">
      Autenticando...
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando...</div>;
  if (!user) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen flex relative" data-testid="app-shell">
      {/* Plano de fundo Hermes — foto fixada + véu creme para manter a leitura */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          backgroundImage: "url(/hermes-background.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{ background: "linear-gradient(180deg, rgba(250,249,246,0.92) 0%, rgba(250,249,246,0.96) 60%, rgba(250,249,246,0.98) 100%)" }}
      />
      <Sidebar />
      <main className="flex-1 min-w-0">{children}</main>
      <DeadlineAlert />
    </div>
  );
}

function Router() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/privacidade" element={<Privacidade />} />
      <Route path="/termos" element={<Termos />} />
      <Route path="/sign/:token" element={<SignPublic />} />
      <Route path="/verify" element={<VerifyPublic />} />
      <Route path="/verify/:token" element={<VerifyPublic />} />
      <Route path="/verificacao/calculo/:token" element={<VerifyCalculation />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/reader" element={<Protected><DocumentReader /></Protected>} />
      <Route path="/review" element={<Protected><OCRReview /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
      <Route path="/clients/:id/new-document" element={<Protected><DocumentChoice /></Protected>} />
      <Route path="/clients/:id/new-document/contrato" element={<Protected><ContractForm /></Protected>} />
      <Route path="/clients/:id/new-document/procuracao" element={<Protected><ProcuracaoForm /></Protected>} />
      <Route path="/documents/:id" element={<Protected><DocumentEditor /></Protected>} />
      <Route path="/templates" element={<Protected><ModelosHub /></Protected>} />
      <Route path="/kanban" element={<Protected><Kanban /></Protected>} />
      <Route path="/doc-templates" element={<Protected><DocTemplates /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/plans" element={<Protected><PlansPage /></Protected>} />
      <Route path="/meu-plano" element={<Protected><MyPlan /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/finance" element={<Protected><FinanceHub /></Protected>} />
      <Route path="/cobrancas" element={<Protected><Cobrancas /></Protected>} />
      <Route path="/noticias" element={<Protected><Noticias /></Protected>} />
      <Route path="/jogos" element={<Protected><Jogos /></Protected>} />
      <Route path="/notificacoes" element={<Protected><Notifications /></Protected>} />
      <Route path="/mineral/extrato" element={<Protected><ExtratoCreditosMineral /></Protected>} />
      <Route path="/inteligencia/radar-mineral" element={<Protected><DashboardRadarMineral /></Protected>} />
      <Route path="/indicacoes" element={<Protected><Referrals /></Protected>} />
      {/* Legacy alias — mantém URL antiga funcionando */}
      <Route path="/atualizacao-sentenca" element={<Navigate to="/calculos/atualizacao" replace />} />

      {/* Hermes · Cálculos (agregador + módulos) */}
      <Route path="/calculos" element={<Protected><CalculosHome /></Protected>} />
      <Route path="/calculos/atualizacao" element={<Protected><SentenceUpdates /></Protected>} />
      <Route path="/calculos/liquidacao-trabalhista" element={<Protected><LiquidacaoList /></Protected>} />
      <Route path="/calculos/liquidacao-trabalhista/nova" element={<Protected><LiquidacaoNova /></Protected>} />
      <Route path="/calculos/liquidacao-trabalhista/:id" element={<Protected><LiquidacaoDetail /></Protected>} />
      <Route path="/calculos/liquidacao-trabalhista/:id/parametros" element={<Protected><LiquidacaoParametros /></Protected>} />
      <Route path="/calculos/liquidacao-trabalhista/:id/titulo" element={<Protected><LiquidacaoTitulo /></Protected>} />
      <Route path="/calculos/liquidacao-trabalhista/:id/calculo" element={<Protected><LiquidacaoCalculo /></Protected>} />
      <Route path="/djen" element={<Protected><Djen /></Protected>} />
      <Route path="/prazos" element={<Protected><Prazos /></Protected>} />
      <Route path="/prazos/kanban" element={<Protected><KanbanPrazos /></Protected>} />
      <Route path="/processos/:cnj" element={<Protected><ProcessoDetail /></Protected>} />
      <Route path="/preparado" element={<Protected><PreparadoHermes /></Protected>} />
      <Route path="/preparados" element={<Protected><PreparadosList /></Protected>} />
      <Route path="/preparados/comparar" element={<Protected><PreparadosCompare /></Protected>} />
      <Route path="/modelos-converge" element={<Protected><ModelosConverge /></Protected>} />
      <Route path="/preparado/:id" element={<Protected><PreparadoDetail /></Protected>} />
      <Route path="/vale-a-pena" element={<Protected><ValePena /></Protected>} />
      <Route path="/jurisprudencia" element={<Protected><Jurisprudencia /></Protected>} />
      <Route path="/inteligencia" element={<Protected><Inteligencia360 /></Protected>} />
      <Route path="/inteligencia/pedidos" element={<Protected><IntelligenceOrders /></Protected>} />
      <Route path="/inteligencia/watches" element={<Protected><IntelligenceWatches /></Protected>} />
      <Route path="/inteligencia/legislativo" element={<Protected><Fase6Legislativo /></Protected>} />
      <Route path="/inteligencia/contratacoes" element={<Protected><Fase6Contratacoes /></Protected>} />
      <Route path="/inteligencia/radar" element={<Protected><RadarRisco /></Protected>} />
      <Route path="/criar-conta" element={<AuthEmailForm mode="register" />} />
      <Route path="/login-email" element={<AuthEmailForm mode="login" />} />
      <Route path="/esqueci-senha" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/mineral" element={<Protected><MineralLayout /></Protected>}>
        <Route index element={<MineralHome />} />
        <Route path="pesquisar" element={<MineralPlaceholder title="Pesquisar" fase="Fase 1 · em homologação" description="Pesquisa por Substância, Região ou Titular sobre a base ANM oficial (SCM+SIGMINE). Requer chave gov.br." />} />
        <Route path="descobrir" element={<MineralPlaceholder title="Descobrir Região" fase="Fase 1" description="Explorar municípios/UFs sem definir substância — para prospecção de oportunidades minerárias." />} />
        <Route path="mapa" element={<MineralMap />} />
        <Route path="titulares" element={<MineralPlaceholder title="Titulares" fase="Fase 1 (feature flag)" description="Consulta por CNPJ do titular. Só ativa após homologação da cobertura de CNPJ nos dados oficiais." />} />
        <Route path="comparar" element={<MineralPlaceholder title="Comparar" fase="Fase 4" description="Comparação lado-a-lado de processos ou regiões minerárias." />} />
        <Route path="classificacoes" element={<MineralPlaceholder title="Classificações" fase="Fase 4" description="Rankings determinísticos por Estado, substância, titular, fase." />} />
        <Route path="tendencias" element={<MineralPlaceholder title="Tendências" fase="Fase 4" description="Evolução histórica da atividade minerária por região e substância." />} />
        <Route path="economica" element={<MineralPlaceholder title="Inteligência Econômica" fase="Fase 5 · CFEM/TAH/AMB" description="Arrecadação CFEM, TAH, produção AMB. Requer providers ANM homologados." />} />
        <Route path="producao" element={<MineralPlaceholder title="Produção Mineral" fase="Fase 5 · AMB" description="Anuário Mineral Brasileiro — produção bruta, beneficiada, água mineral." />} />
        <Route path="ofertas" element={<MineralPlaceholder title="Áreas em Oferta" fase="Fase 5 · SOPLE" description="Estoque de áreas em disponibilidade e resultados de rodadas SOPLE." />} />
        <Route path="barragens" element={<MineralPlaceholder title="Barragens" fase="Fase 5 · SIGBM" description="Barragens de mineração — características, rejeito, classificação, estabilidade." />} />
        <Route path="monitoramentos" element={<MineralPlaceholder title="Monitoramentos" fase="Fase 4" description="Detecção de novos processos, mudanças de fase, alterações de titularidade." />} />
        <Route path="alertas" element={<MineralPlaceholder title="Alertas Minerários" fase="Fase 4" description="Alerta quando novo processo compatível com sua tese aparece na base." />} />
        <Route path="relatorios" element={<MineralPlaceholder title="Relatórios" fase="Fase 3 · Commerce" description="Análise 360°, PDF, entrega via WhatsApp/E-mail. Requer Stripe e produtos ativos." />} />
        <Route path="meus-pedidos" element={<MineralPlaceholder title="Meus Pedidos" fase="Fase 3 · Commerce" description="Histórico de pedidos, status de pagamento, entitlements." />} />
        <Route path="meus-relatorios" element={<MineralPlaceholder title="Meus Relatórios" fase="Fase 3 · Commerce" description="Relatórios liberados — visualização, download, impressão e compartilhamento." />} />
        <Route path="admin/produtos" element={<MineralAdminCommerce />} />
      </Route>
      <Route path="/r/:code" element={<ReferralCapture />} />

      {/* Consulta Mineral Pública (guest-friendly · sem auth) */}
      <Route path="/consulta-mineral" element={<ConsultaMineralPublica />} />
      <Route path="/consulta-mineral/aguardando" element={<ConsultaMineralAguardando />} />
      <Route path="/consulta-mineral/relatorio/:token" element={<RelatorioMineralPublico />} />
      <Route path="/consulta-mineral/compartilhado/:token" element={<RelatorioMineralPublico shared />} />
      <Route path="/consulta-mineral/meus" element={<MeusPedidosGuest />} />
      <Route path="/consulta-mineral/lote" element={<ConsultaLoteMineral />} />

      {/* Retorno de pagamento Stripe · recarga da carteira */}
      <Route path="/carteira/recarga-sucesso" element={<WalletTopupSuccess />} />
      <Route path="/plans/success" element={<PlanSuccess />} />

      {/* OWNER admin area — protection is done inside AdminLayout */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="geo" element={<AdminGeo />} />
        <Route path="birthdays" element={<AdminBirthdays />} />
        <Route path="financial" element={<AdminFinancial />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="communications" element={<AdminCommunications />} />
        <Route path="templates" element={<AdminTemplates />} />
        <Route path="intelligence" element={<AdminIntelligence />} />
        <Route path="creditos-mineral" element={<AdminCreditosMineral />} />
        <Route path="djen" element={<AdminDjen />} />
        <Route path="images" element={<AdminImages />} />
        <Route path="comercial" element={<AdminComercial />} />
        <Route path="apis" element={<AdminApisHealth />} />
      </Route>
    </Routes>
  );
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    // Skip the auth-me probe on truly public routes (referral capture) to avoid noisy 401s.
    if (window.location.pathname.startsWith("/r/")) { setLoading(false); return; }
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(null)).finally(() => setLoading(false));
  }, []);
  const logout = async () => { await api.post("/auth/logout"); setUser(null); window.location.href = "/"; };
  return <AuthCtx.Provider value={{ user, setUser, loading, logout }}>{children}</AuthCtx.Provider>;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster richColors position="top-right" />
          <Router />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
